const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const FIFTEEN_DAYS_MS = 15 * 24 * 60 * 60 * 1000
const MAX_ORDER_ID = 2147483647

export const ADMIN_VISIT_SORT_FIELDS = [
  'per_date',
  'transfer_stamp',
  'process_stamp',
  'visit_id',
  'rid',
  'group_id',
  'per_status',
  'full_name',
  'full_name_eng',
  'user_fullname',
  'user_group'
]

function capText (value, max) {
  const text = value == null ? '' : String(value).trim()
  if (!text) return ''
  return text.slice(0, max)
}

function optionalInt (value) {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return n
}

/**
 * Query for GET /api/visits (sp_GetVisitRecordsAdmin).
 * Same fields as /api/visit, plus user_group.
 * reader_id is never sent — the API reads extension_SystemID from the JWT.
 */
export function buildAdminVisitQuery (param) {
  const src = param || {}
  const query = {}

  ;['rid', 'visit_id', 'user_id', 'group_id'].forEach((key) => {
    const n = optionalInt(src[key])
    if (n != null && n !== 0) query[key] = n
  })

  const perDate = src.per_date != null ? String(src.per_date).trim() : ''
  if (DATE_RE.test(perDate)) query.per_date = perDate

  const statusList = capText(src.per_status_list, 50)
  if (statusList) query.per_status_list = statusList

  const userGroup = capText(src.user_group, 50)
  if (userGroup) query.user_group = userGroup

  const sortBy = src.sortBy != null ? String(src.sortBy).trim() : ''
  query.sortBy = ADMIN_VISIT_SORT_FIELDS.indexOf(sortBy) >= 0 ? sortBy : 'per_date'

  const desc = src.sortDesc
  query.sortDesc = desc === false || desc === 0 || desc === '0' ? 0 : 1

  const page = Number(src.page)
  query.page = page >= 1 ? page : 1

  const per = Number(src.itemsPerPage)
  if (!Number.isFinite(per) || per < 1) query.itemsPerPage = 10
  else query.itemsPerPage = Math.min(Math.floor(per), 200)

  return query
}

export function messageFromAdminVisitError (err) {
  return messageFromAdminApiError(err, 'ไม่มีสิทธิ์ค้นหา Visit', 'ค้นหา Visit ไม่สำเร็จ')
}

export function messageFromAdminOrderError (err) {
  const data = err && err.response && err.response.data
  if (data && typeof data.error === 'string' && data.error) return data.error
  if (data && typeof data.message === 'string' && data.message) return data.message
  const status = err && err.response && err.response.status
  if (status === 400) return 'รหัสรายการไม่ถูกต้อง หรือไม่พบผู้ใช้จาก token'
  if (status === 401 || status === 403) return 'ไม่มีสิทธิ์ทำรายการนี้'
  if (status === 409) return 'รายการนี้คืนไม่ได้'
  if (status === 500) return 'ข้อผิดพลาดของระบบ'
  return (err && err.message) || 'ดำเนินการไม่สำเร็จ'
}

export function messageFromAdminOrderQuantityError (err) {
  const data = err && err.response && err.response.data
  if (data && typeof data.error === 'string' && data.error) return data.error
  if (data && typeof data.message === 'string' && data.message) return data.message
  const status = err && err.response && err.response.status
  if (status === 400) return 'จำนวนไม่ถูกต้อง หรือจำนวนเดิมเป็น 0'
  if (status === 401 || status === 403) return 'ไม่มีสิทธิ์ทำรายการนี้'
  if (status === 404) return 'ไม่พบรายการหรือ Visit'
  if (status === 500) return 'ข้อผิดพลาดของระบบ'
  return (err && err.message) || 'บันทึกจำนวนไม่สำเร็จ'
}

export function adminOrderIdValue (value) {
  const n = Number(value)
  if (!Number.isInteger(n) || n < 1 || n > MAX_ORDER_ID) return null
  return n
}

/** Quantity for PATCH /orders/{order_id}. A number greater than 0; decimals are allowed. */
export function adminOrderQuantityValue (value) {
  if (value == null) return null
  const text = String(value).trim().replace(/,/g, '')
  if (!text) return null
  const n = Number(text)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

export function canEditAdminOrderQuantity (order) {
  if (!order) return false
  return adminOrderIdValue(order.order_id) != null && adminOrderQuantityValue(order.quantity) != null
}

export function adminOrderQuantityEditReason (order) {
  if (!order || adminOrderIdValue(order.order_id) == null) return 'รหัสรายการไม่ถูกต้อง'
  if (adminOrderQuantityValue(order.quantity) == null) return 'จำนวนเดิมเป็น 0 แก้ไม่ได้'
  return ''
}

function responseHas (data, key) {
  return data[key] != null && data[key] !== ''
}

/** Copy quantity and recalculated amounts from a 200 response onto the order row. */
export function applyAdminOrderQuantityResponse (order, data) {
  const next = { ...(order || {}) }
  if (!data || typeof data !== 'object') return next
  if (responseHas(data, 'quantity')) next.quantity = data.quantity
  if (responseHas(data, 'price_stamp')) next.price_stamp = data.price_stamp
  if (responseHas(data, 'price_base')) next.price_base = data.price_base
  return next
}

function messageFromAdminApiError (err, deniedMessage, fallback) {
  const data = err && err.response && err.response.data
  if (data && typeof data.error === 'string' && data.error) return data.error
  if (data && typeof data.message === 'string' && data.message) return data.message
  const status = err && err.response && err.response.status
  if (status === 401 || status === 403) return deniedMessage
  return (err && err.message) || fallback
}

export function isProcessStampWithinLast15Days (value, now = new Date()) {
  if (value == null || value === '') return false
  const stamp = new Date(value)
  const current = now instanceof Date ? now : new Date(now)
  if (Number.isNaN(stamp.getTime()) || Number.isNaN(current.getTime())) return false
  const age = current.getTime() - stamp.getTime()
  return age >= 0 && age <= FIFTEEN_DAYS_MS
}

function matgroupValue (order) {
  if (order.matgroup != null && order.matgroup !== '') return order.matgroup
  if (order.Matgroup != null && order.Matgroup !== '') return order.Matgroup
  if (order.MatGroup != null && order.MatGroup !== '') return order.MatGroup
  if (order.mat_group != null && order.mat_group !== '') return order.mat_group
  return null
}

function matgroupIsDrug (order) {
  const raw = matgroupValue(order)
  if (raw == null || String(raw).trim() === '') return true
  return Number(String(raw).trim()) === 201
}

function stockGroupValue (order) {
  if (order.group_stock != null && order.group_stock !== '') return order.group_stock
  if (order.stock_group_id != null && order.stock_group_id !== '') return order.stock_group_id
  return null
}

function stockStatusValue (order) {
  if (order.stock_active != null && order.stock_active !== '') return order.stock_active
  if (order.active != null && order.active !== '') return order.active
  if (order.stock_status != null && order.stock_status !== '') return order.stock_status
  return null
}

const RETURNABLE_STOCK_STATUS = ['A', 'T', 'P']

function stockStatusCanReturn (order) {
  const raw = stockStatusValue(order)
  if (raw == null) return true
  return RETURNABLE_STOCK_STATUS.indexOf(String(raw).trim().toUpperCase()) >= 0
}

function stockIdValue (order) {
  const stockId = Number(order.stock_id)
  return Number.isFinite(stockId) && stockId > 0 ? stockId : 0
}

function stockMatchesVisitGroup (order, visit) {
  if (!stockIdValue(order)) return false
  const stockGroup = stockGroupValue(order)
  if (stockGroup == null) return true
  if (visit.group_id == null || visit.group_id === '') return false
  return Number(stockGroup) === Number(visit.group_id)
}

function processStampReason (value, now) {
  if (value == null || String(value).trim() === '') return ''
  const stamp = new Date(value)
  const current = now instanceof Date ? now : new Date(now)
  if (Number.isNaN(stamp.getTime()) || Number.isNaN(current.getTime())) return 'วันรับเรื่องของ Visit ไม่ถูกต้อง'
  const age = current.getTime() - stamp.getTime()
  if (age < 0) return 'วันรับเรื่องเป็นวันในอนาคต'
  if (age > FIFTEEN_DAYS_MS) return 'วันรับเรื่องของ Visit เลย 15 วัน'
  return ''
}

export function returnAdminOrderReason (order, visit, now = new Date()) {
  if (!order || !visit) return 'ไม่พบรายการ'
  const orderId = Number(order.order_id)
  if (!Number.isInteger(orderId) || orderId < 1 || orderId > MAX_ORDER_ID) return 'รหัสรายการไม่ถูกต้อง'
  if (!matgroupIsDrug(order)) return 'คืนได้เฉพาะกลุ่มยา 201'
  const status = String(order.order_status).trim()
  if (status !== '3' && status !== '4') return 'คืนได้เมื่อสถานะเป็นจ่ายยาแล้วหรือตัดสต็อกแล้ว'
  const stampReason = processStampReason(visit.process_stamp, now)
  if (stampReason) return stampReason
  const qty = Number(order.quantity)
  if (!Number.isFinite(qty) || qty <= 0) return 'จำนวนต้องมากกว่า 0'
  if (!stockIdValue(order)) return 'ไม่มีสต็อก'
  if (!stockMatchesVisitGroup(order, visit)) return 'สต็อกไม่ได้อยู่กลุ่มเดียวกับ Visit'
  if (!stockStatusCanReturn(order)) return 'สถานะสต็อกต้องเป็นใช้ได้ ใช้จนหมด หรือหมด'
  return ''
}

export function canReturnAdminOrder (order, visit, now = new Date()) {
  return returnAdminOrderReason(order, visit, now) === ''
}
